import Image from 'next/image'
import { useMemo } from 'react'
import Link from 'next/link'
import { nextImageHelper, supify } from '../../../utils'
import { generateNewOrderId } from 'utils/orderId/generateNewOrderId'
import type { CompareCredit } from '../../../../types/compare-credit'
import {
  AdvertiserDisclaimer,
  CardArt,
  LabelAd,
  LinkToRedirect,
  StarRatingEditor,
} from '../..'
import { SecureSite } from '../../secure-site'
import type { OverrideProperties } from 'type-fest'
import { getIsNonEmptyArray } from 'utils/getIsNonEmptyArray'
import { Disclosure } from 'types/sanity.types'

const CATEGORY_ID = '53'
const EXTERNAL_ID = '53'
const PROFILE_ID = 'hpto'
type HeroHptoProps = {
  cards: CompareCredit.FormattedCard[]
  headline: string
  disclosure: Disclosure[]
}

export const HeroHpto: React.FC<HeroHptoProps> = ({ cards, ...restProps }) => {
  const hasCards = getIsNonEmptyArray(cards)

  if (!hasCards) {
    throw new Error(
      'HeroHpto requires a non-empty array of cards, but received an empty array',
    )
  }

  return <HeroHptoInner {...restProps} cards={cards} />
}

const HeroHptoInner: React.FC<
  OverrideProperties<HeroHptoProps, { cards: [CompareCredit.FormattedCard] }>
> = ({ cards: [card], disclosure: disclosure }) => {
  const orderId = useMemo(() => generateNewOrderId(), [])
  const applyNowLinkParameters =
    card.applyNowLinkProfiles[PROFILE_ID]?.applyNowLinkParameters

  return (
    <div
      id="hero"
      className="c-hero / -mt-16 lg:-mt-20 / pt-20 pb-16 md:pb-8 lg:pb-7 xl:pb-12 lg:pt-32 relative z-10"
    >
      <div className="c-hero__content / container-fluid relative z-2 / md:flex md:flex-row md:flex-wrap / max-w-6xl / mx-auto">
        <div className="md:order-2 / flex flex-col justify-start items-start / max-w-xs md:max-w-none / md:w-2/3 / mb-6 mx-auto lg:ml-0 / md:pl-6 xl:pl-10">
          <div className="c-hero__content-1 / c-hero__ribbon / -mb-2 / text-left">
            <p className="c-ribbon c-ribbon--slim c-ribbon--rt / relative inline-block / pt-0 px-2.5 / text-xl md:text-[21px] font-[900] text-primary leading-snug uppercase tracking-wider / bg-tetriary ">
              <span className="inline-block pt-0.25 overflow-hidden">New!</span>
            </p>
          </div>
          <h1 className="c-hero__content-2 / c-hero__title / flex flex-row items-center / md:max-w-md lg:max-w-none / md:mb-6 lg:mb-8 / leading-tight text-primary font-bold">
            <span className="c-hero__title-number-wrap / relative / flex flex-row / font-[900] text-[64px] md:text-[80px] lg:text-[108px] xl:text-[132px]">
              <span className="c-hero__title-number / relative / flex flex-col w-[.7em] h-[1em]">
                0 1 2 3 4 5 6
              </span>
              %{' '}
              <hr className="c-hero__title-border / absolute bottom-0 / border-3 md:border-4 border-tetriary" />
            </span>{' '}
            <span className="c-hero__title-content / pl-2 md:pl-4 lg:pl-6 / text-left text-[24px] md:text-[27px] lg:text-[44px] xl:text-[48px] leading-[1.15]">
              Choice Category Cash&nbsp;Back&nbsp;Offer
            </span>
          </h1>
          <div className="md:block hidden">
            <div className="c-hero__content-3">
              <HptoContent />
            </div>
          </div>
        </div>
        <div className="c-hero__content-3 md:order-1 relative z-10 / md:w-1/3 / lg:mb-2 / lg:pr-8">
          <div className="c-card / flex flex-col items-center justify-center / w-full mx-auto / mb-3">
            <div className="c-card-art__container / w-full max-w-[16rem] lg:max-w-full / mb-3 lg:mb-4 mx-auto">
              <div className="c-card-art / relative / mx-auto / transition-25s overflow-hidden rounded / shadow-lg">
                <span
                  className="c-card-art__shine / absolute z-2 / w-16 / rotate-12"
                  role="presentation"
                ></span>
                <div className="c-card-art__container / relative z-2 / w-full mx-auto / rounded-sm shadow overflow-hidden text-center">
                  <LinkToRedirect
                    externalId={EXTERNAL_ID}
                    linkParams={applyNowLinkParameters}
                    orderId={orderId}
                    profileId={PROFILE_ID}
                    query={undefined}
                    slug={`${card.slug}`}
                  >
                    <div className="relative flex overflow-hidden rounded-lg shadow z-0">
                      <span className="c-card-art__content / absolute / flex flex-wrap content-center items-center / z-10 left-0 / w-full h-full text-white lg:text-lg">
                        <span className="w-full xs:mb-1 lg:mb-2">
                          <div className="inline-block h-8 w-8 / lg:h-10 lg:w-10">
                            <Image
                              alt="lock"
                              src="/static/icons/icon-lock-round-w.svg"
                              height={40}
                              width={40}
                              style={nextImageHelper.responsive}
                              sizes="100vw"
                              aria-hidden={true}
                            />
                          </div>
                        </span>
                        <span className="w-full font-semibold">Apply Now</span>
                      </span>
                      <CardArt
                        _rev={card._rev}
                        cardArt={card.cardArt}
                        imgClasses="relative z-0 w-full h-full object-cover top-0"
                        customCodeSnippets={card.customCodeSnippets}
                        issuer={card.issuer.slug.current}
                        name={card.name}
                        orderId={orderId}
                        slug={card.slug}
                        categoryId={CATEGORY_ID}
                        externalId={EXTERNAL_ID}
                        imagePriority={true}
                      />
                    </div>
                  </LinkToRedirect>
                </div>
              </div>
            </div>
            <p className="max-w-xs / w-full mx-auto mb-2 lg:mb-3 / px-4 sm:px-0 xl:px-6 / text-center leading-tight">
              <LinkToRedirect
                externalId={EXTERNAL_ID}
                linkParams={applyNowLinkParameters}
                orderId={orderId}
                profileId={PROFILE_ID}
                query={undefined}
                slug={`${card.slug}`}
              >
                <span
                  className="text-primary font-semibold text-lg md:text-sm lg:text-lg / hover:text-primary-mid hover:underline"
                  dangerouslySetInnerHTML={{
                    __html: supify(card.name),
                  }}
                />
              </LinkToRedirect>
            </p>

            <div className="relative z-10 / mb-3 / lg:scale-[1.1]">
              <StarRatingEditor
                productSlug={card.slug}
                stars={card.expertReviewRating}
              />
            </div>

            <div className="w-full">
              <LinkToRedirect
                externalId={EXTERNAL_ID}
                linkParams={applyNowLinkParameters}
                orderId={orderId}
                profileId={PROFILE_ID}
                query={undefined}
                slug={`${card.slug}`}
              >
                <div className="c-btn c-btn--primary / max-w-[16rem] sm:max-w-xs md:max-w-none w-full / md:w-full / text-lg md:text-base lg:text-xl / mb-3 lg:mt-0.5">
                  Apply Now
                  <span className="ml-2">
                    <div className="w-5 h-5">
                      <Image
                        alt="lock"
                        src="/static/icons/icon-lock-round-w.svg"
                        height={20}
                        width={20}
                        style={nextImageHelper.responsive}
                        sizes="100vw"
                        aria-hidden={true}
                      />
                    </div>
                  </span>
                </div>
              </LinkToRedirect>
            </div>
            <p className="text-xs lg:text-sm text-gray-600 text-center / mb-2.5">
              <SecureSite
                issuerName={card.issuer.name}
                issuerSecureSite={card.issuerSecureSite}
              />
            </p>

            {card.ratesAndFees && card.ratesAndFees && (
              <div className="w-full / mt-2 / text-center text-shadow">
                {card.ratesAndFees && card.ratesAndFees !== 'N/A' && (
                  <p className="mb-0.5">
                    <Link
                      className="inline-block text-primary-bright text-fs12 leading-snug / hover:text-primary-mid"
                      href={card.ratesAndFees}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Rates &amp; Fees
                    </Link>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="c-hero__content-4 / md:order-3 max-w-md md:max-w-none mx-auto / w-full">
          <div className="mb-6 / md:hidden">
            <HptoContent />
          </div>
          <div>
            <div className="c-hero__category-wrap / md:flex md:flex-row md:items-stretch / mb-5 / p-4 md:px-2 md:py-3 lg:p-2 xl:p-2.5 / rounded bg-white shadow-lg">
              <div className="flex md:gap-1 md:flex-col items-center justify-center / md:w-[25%] lg:w-[20%] / mb-4 md:mb-0 / p-2 lg:p-3 xl:px-5 / bg-slate-50 rounded">
                <span className="shrink-0 w-48 md:w-full / inline-block / mr-2 md:mr-0">
                  <Image
                    alt="BofA FCWC logo"
                    src="/static/images/bofa-fcwc-2025.webp"
                    height={329}
                    width={640}
                    style={nextImageHelper.responsive}
                    sizes="100vw"
                    aria-hidden={true}
                  />
                </span>
              </div>
              <div className="flex flex-col items-center justify-center / md:w-[75%] lg:w-[80%] / lg:pl-4">
                <p className="mb-3 xl:mb-4 / text-lg md:text-sm lg:text-base font-bold text-primary text-center leading-snug">
                  First-year double cash back offer:{' '}
                  <span className="text-bofa-red">Earn 6% cash back</span> in
                  the category&nbsp;of&nbsp;your&nbsp;choice
                </p>
                <ul className="flex flex-col md:flex-row md:items-stretch gap-2 / w-auto / text-slate-600 md:text-center md:text-fs12 lg:text-fs13 xl:text-sm leading-snug">
                  <li className="flex md:gap-1 md:flex-col items-center / md:w-1/6">
                    <span className="shrink-0 / inline-block w-8 sm:w-10 / mr-2 md:mr-0">
                      <Image
                        alt="gas"
                        src="/static/icons/stroke-gas-ev-red.svg"
                        height={48}
                        width={48}
                        style={nextImageHelper.responsive}
                        sizes="100vw"
                        aria-hidden={true}
                      />
                    </span>{' '}
                    Gas &amp; EV Charging Stations
                  </li>
                  <li className="flex md:gap-1 md:flex-col items-center / md:w-1/6">
                    <span className="shrink-0 / inline-block w-8 sm:w-10 / mr-2 md:mr-0">
                      <Image
                        alt="drug store"
                        src="/static/icons/stroke-drug-store-red.svg"
                        height={48}
                        width={48}
                        style={nextImageHelper.responsive}
                        sizes="100vw"
                        aria-hidden={true}
                      />
                    </span>{' '}
                    Drug Stores &amp; Pharmacies
                  </li>
                  <li className="flex md:gap-1 md:flex-col items-center / md:w-1/6">
                    <span className="shrink-0 / inline-block w-8 sm:w-10 / mr-2 md:mr-0">
                      <Image
                        alt="dining"
                        src="/static/icons/stroke-dining-red.svg"
                        height={48}
                        width={48}
                        style={nextImageHelper.responsive}
                        sizes="100vw"
                        aria-hidden={true}
                      />
                    </span>{' '}
                    Dining
                  </li>
                  <li className="flex md:gap-1 md:flex-col items-center / md:w-1/6">
                    <span className="shrink-0 / inline-block w-8 sm:w-10 / mr-2 md:mr-0">
                      <Image
                        alt="laptop"
                        src="/static/icons/stroke-online-laptop-red.svg"
                        height={48}
                        width={48}
                        style={nextImageHelper.responsive}
                        sizes="100vw"
                        aria-hidden={true}
                      />
                    </span>{' '}
                    Online Shopping &amp; More
                  </li>
                  <li className="flex md:gap-1 md:flex-col items-center / md:w-1/6">
                    <span className="shrink-0 / inline-block w-8 sm:w-10 / mr-2 md:mr-0">
                      <Image
                        alt="airplane"
                        src="/static/icons/stroke-travel-red.svg"
                        height={48}
                        width={48}
                        style={nextImageHelper.responsive}
                        sizes="100vw"
                        aria-hidden={true}
                      />
                    </span>{' '}
                    Travel
                  </li>
                  <li className="flex md:gap-1 md:flex-col items-center / md:w-1/6">
                    <span className="shrink-0 / inline-block w-8 sm:w-10 / mr-2 md:mr-0">
                      <Image
                        alt="lamp"
                        src="/static/icons/stroke-home-furnish-red.svg"
                        height={48}
                        width={48}
                        style={nextImageHelper.responsive}
                        sizes="100vw"
                        aria-hidden={true}
                      />
                    </span>{' '}
                    Home Improvement &amp;&nbsp;Furnishings
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex justify-between items-center px-4">
              <AdvertiserDisclaimer
                alignPopup="t-disclosure-left"
                disclosure={disclosure}
              />
              <LabelAd />
            </div>
          </div>
        </div>
      </div>

      <div className="c-hero__bot / absolute / bg-no-repeat / w-full overflow-hidden">
        <img
          src="./static/images/bg-bot-mask-white-sm.png"
          className="c-hero__bg-mask / md:hidden"
          alt="background mask"
          role="presentation"
          aria-hidden={true}
        />
        <img
          src="./static/images/bg-bot-mask-white-lg.png"
          className="c-hero__bg-mask hidden md:block"
          alt="background mask"
          role="presentation"
          aria-hidden={true}
        />
      </div>

      <style jsx>{`
        /*===== GENERAL HPTO CLASSES =====*/
        .c-hero__title-number {
          transform: translateY(-7.5em);
        }
        .text-bofa-red {
          color: #db2645;
        }
        .c-hero {
          background-image: url('/static/images/bg-bofa-bankamericard.png'),
            linear-gradient(
              to bottom,
              rgba(255, 255, 255, 1) 25%,
              rgba(236, 235, 233, 0.5) 100%
            );
          background-size: cover;
          background-position: center top;
          background-repeat: no-repeat;
          width: 100vw;
          position: relative;
          left: 50%;
          right: 50%;
          margin-left: -50vw;
          margin-right: -50vw;
        }

        @media (min-width: 1152px) {
          .c-hero__title {
            font-size: 72px;
          }
          .container-fluid {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }

        /*===== TITLE NUMBER =====*/
        .c-hero__title {
          overflow-y: hidden;
        }
        .c-hero__title-number {
          animation-name: animateNumber;
          animation-duration: 2s;
          animation-fill-mode: forwards;
          opacity: 0.5;
        }

        /* ===== CONTENT ===== */
        .c-hero__content {
          z-index: 3;
        }

        .c-hero__content-1 {
          transform: translateY(-2rem);
          animation-name: animateInRibbon;
          animation-duration: 0.4s;
          animation-fill-mode: forwards;
          opacity: 0;
          animation-delay: 0.25s;
        }

        .c-hero__content-2,
        .c-hero__content-3,
        .c-hero__content-4 {
          transform: translateY(2rem);
        }
        .c-hero__content-2,
        .c-hero__content-3,
        .c-hero__content-4 {
          animation-name: animateIn;
          animation-duration: 0.75s;
          animation-fill-mode: forwards;
          opacity: 0;
        }
        .c-hero__content-2 {
          animation-delay: 0.25s;
        }
        .c-hero__content-3 {
          animation-delay: 0.5s;
        }
        .c-hero__content-4 {
          animation-delay: 0.75s;
        }

        .c-hero__title-border {
          animation-name: animateBorder;
          animation-duration: 0.4s;
          animation-fill-mode: forwards;
          opacity: 0;
          animation-delay: 1.75s;
        }

        .c-card-art__shine {
          z-index: 3;
          top: -10%;
          left: -100%;
          height: 120%;
          opacity: 0%;
          background: linear-gradient(
            to right,
            rgba(254, 201, 208, 0) 0%,
            rgba(254, 201, 208, 1) 50%,
            rgba(254, 201, 208, 0) 100%
          );
          animation-name: animateLeftToRight;
          animation-duration: 8s;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          animation-delay: 2s;
        }

        /*===== HERO BOTTOM =====*/
        .c-hero__bot {
          bottom: -1px;
          z-index: 2;
        }
        .c-hero__bg-mask {
          width: 100%;
          margin-bottom: -1px;
          height: 3rem;
        }
        @media (min-width: 640px) {
          .c-hero__bg-mask {
            height: 3.5rem;
          }
        }
        @media (min-width: 1024px) {
          .c-hero__bg-mask {
            height: 3rem;
          }
        }
        @media (min-width: 1152px) {
          .c-hero__bg-mask {
            height: 5rem;
          }
        }

        @media (min-width: 1440px) {
          .c-hero {
            padding-bottom: 8rem;
          }
        }
        @media (min-width: 1600px) {
          .c-hero {
            padding-bottom: 12rem;
          }
        }

        /*===== Animation =====*/
        @keyframes animateLeftToRight {
          0% {
            left: -100%;
            opacity: 10%;
          }
          15% {
            left: 200%;
            opacity: 40%;
          }
          30% {
            left: 200%;
            opacity: 10%;
          }
          100% {
            left: 200%;
            opacity: 10%;
          }
        }
        @keyframes animateBorder {
          from {
            width: 0;
            opacity: 0;
          }
          to {
            width: 100%;
            opacity: 1;
          }
        }
        @keyframes animateInRibbon {
          from {
            opacity: 0;
            transform: translateY(-2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes animateNumber {
          from {
            opacity: 0.5;
            transform: translateY(0);
          }
          to {
            opacity: 1;
            transform: translateY(-7.5em);
          }
        }
        @keyframes animateIn {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes clrHighlight {
          0% {
            color: #052f5e;
          }
          50% {
            color: #21a3fc;
          }
          100% {
            color: #052f5e;
          }
        }
        @keyframes scaleUp {
          0% {
            transform: scale(0.95);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  )
}

function HptoContent() {
  return (
    <>
      <ul className="flex flex-col gap-3 lg:gap-4 / xl:text-lg text-slate-600">
        <li className="flex flex-row">
          <span className="shrink-0 / inline-block w-8">
            <Image
              alt="checkmark"
              src="/static/icons/icon-checkmark-red.svg"
              height={48}
              width={48}
              style={nextImageHelper.responsive}
              sizes="100vw"
              aria-hidden={true}
            />
          </span>{' '}
          <span className="pl-4">
            <b className="text-primary">New online offer:</b> Double your choice
            category cash back to 6% for a&nbsp;year!
          </span>
        </li>
        <li className="flex flex-row">
          <span className="shrink-0 / inline-block w-8">
            <Image
              alt="checkmark"
              src="/static/icons/icon-checkmark-red.svg"
              height={48}
              width={48}
              style={nextImageHelper.responsive}
              sizes="100vw"
              aria-hidden={true}
            />
          </span>{' '}
          <span className="pl-4">
            <b className="text-primary">
              Earn 3% cash back + 3% first-year bonus offer
            </b>{' '}
            in the category of your&nbsp;choice.
          </span>
        </li>
        <li className="flex flex-row">
          <span className="shrink-0 / inline-block w-8">
            <Image
              alt="checkmark"
              src="/static/icons/icon-checkmark-red.svg"
              height={48}
              width={48}
              style={nextImageHelper.responsive}
              sizes="100vw"
              aria-hidden={true}
            />
          </span>{' '}
          <span className="pl-4">
            <b className="text-primary">$200 online cash rewards bonus</b> after
            you make at least $1,000 in purchases in the first 90 days of
            account&nbsp;opening.
          </span>
        </li>
      </ul>
    </>
  )
}
